import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { UIFilterAutomationRuleKindType } from './types';
import { useFollowUpCRUDActions } from '@mm/state/actions/FollowUpCRUDActions';
import { useAutomationRulesFilter } from './use-automation-rules-filter';
import { useLeadAssignmentsCRUDActions } from '@mm/state/actions/LeadAssignmentsCRUDActions';

export const useFetchAutomationRules = () => {
  const { activeKind, setFilterKind } = useAutomationRulesFilter();

  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const { retrieveAll: retrieveAllFollowUps, reset: resetFollowUps } =
    useFollowUpCRUDActions();
  const {
    retrieveAll: retrieveAllLeadAssignments,
    reset: resetLeadAssignments,
  } = useLeadAssignmentsCRUDActions();
  const followUps = useSelector(
    (state: StoreState) => state.followUps.entities,
  );
  const leadAssignments = useSelector(
    (state: StoreState) => state.leadAssignments.entities,
  );

  const selectorsByKind = useMemo(
    () => ({
      follow_ups: followUps,
      lead_assignments: leadAssignments,
    }),
    [followUps, leadAssignments],
  );

  const fetchersByKind = useMemo(
    () => ({
      follow_ups: { retrieve: retrieveAllFollowUps, reset: resetFollowUps },
      lead_assignments: {
        retrieve: retrieveAllLeadAssignments,
        reset: resetLeadAssignments,
      },
    }),
    [retrieveAllFollowUps],
  );

  const records = selectorsByKind[activeKind];

  const fetchCallback = useCallback(
    ({ uiFilterKind }: { uiFilterKind: UIFilterAutomationRuleKindType }) => {
      const retrieveAll = fetchersByKind[uiFilterKind]?.retrieve;
      const reset = fetchersByKind[uiFilterKind]?.reset;
      reset?.();
      retrieveAll?.(activeChatbotId);
    },
    [activeChatbotId, fetchersByKind],
  );

  const fetchCurrentActiveKind = useCallback(() => {
    fetchCallback({
      uiFilterKind: activeKind,
    });
  }, [activeKind, fetchCallback]);

  const ref = useRef(debounce((fn: Function) => fn(), 1000));
  const fetchCurrentActiveKindThrottled = () =>
    ref.current(fetchCurrentActiveKind);

  useEffect(fetchCurrentActiveKind, [activeKind, activeChatbotId]);

  return {
    records,
    setFilterKind,
    activeKind,
  };
};
